import {Injectable} from '@angular/core';
import {ApiService} from '../../../core/services/api.service';
import {Observable} from 'rxjs';
import {ApiResponse} from '../../../core/shared/classes/api';
import {FactureSearchTerms, FactureServiceInterface} from './facture-service-interface';
import {FactureNormalize} from '../classes/facture-normalize';
import {FactureDetailNormalize} from '../classes/facture-detail-normalize';
import { IMultipleSort } from '../../../infrastructure/shared/classes/multiple-sort';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class FactureClientService implements FactureServiceInterface {
    constructor(private apiService: ApiService) {}

    fetchAll(params: FactureSearchTerms, page, maxPageCount, options?: {sort: IMultipleSort[]}): Observable<ApiResponse> {
        if (page)
            params['page'] = page;
        if (maxPageCount)
            params['maxPageCount'] = maxPageCount;
        params.withSolde = true;
        if (options) {
            params.sort = options.sort.map(sort => `${sort.column}|${sort.direction}`).join(';');
        }

        return this.apiService.get(`facture/client`, null, params);
    }

    fetchById(id): Observable<ApiResponse> {
        return  this.apiService.get(`facture/client/${id}`);
    }

    getPersonnalisationFacture(id:string): Observable<ApiResponse> {
        return this.apiService.get('facture/personnalisation/' + id);
    }

    setPersonnalisationFacture(id:string, data): Observable<ApiResponse> {
        return this.apiService.post('facture/personnalisation/identite/' + id, data);
    }

    fetchAllRoles(bureauId, batimentId = null, banqueId?): Observable<ApiResponse> {
        const params = {};

        if (bureauId)
            params['bureauId'] = bureauId;
        if (batimentId)
            params['batimentId'] = batimentId;
        if (banqueId !== undefined)
            params['banqueId'] = batimentId;

        return this.apiService.get('facture/client/roles', null, params);
    }

    fetchAllMaitresOuvrage(bureauId, batimentId = null, banqueId?): Observable<ApiResponse> {
        const params = {};

        if (bureauId)
            params['bureauId'] = bureauId;
        if (batimentId)
            params['batimentId'] = batimentId;
        if (banqueId !== undefined)
            params['banqueId'] = batimentId;

        return this.apiService.get('facture/client/maitres-ouvrage', null, params);
    }

    fetchAllLots(bureauId, withPartenaire = null): Observable<ApiResponse> {
        const params = {};

        if (bureauId)
            params['bureauId'] = bureauId;
        if (withPartenaire){
            params['withPartenaire'] = withPartenaire;
        } else {
            params['withPartenaire'] = false;
        }
        return this.apiService.get('facture/client/lots', null, params);
    }

    fetchAllLotsIds(bureauId, withPartenaire = null): Observable<ApiResponse> {
        const params = {};

        if (bureauId)
            params['bureauId'] = bureauId;
        if (withPartenaire){
            params['withPartenaire'] = withPartenaire;
        } else {
            params['withPartenaire'] = false;
        }

        return this.apiService.get('facture/client/lots/id', null, params);
    }

    fetchAllBatiment(batimentId): Observable<ApiResponse> {
        const params = {};

        if (batimentId)
            params['bureauId'] = batimentId;

        return this.apiService.get('facture/client/batiments', null, params);
    }

    fetchByCommandeId(commandeId): Observable<ApiResponse> {
        return this.apiService.get(`facture-client-commande/commande/${commandeId}`);
    }

    factureDetailNormalizer(data: {}): FactureDetailNormalize {
        const res = {};

        Object.keys(data).forEach(elem => {
            const tmp = elem.replace('fcd_', '');
            res[tmp] = data[elem];
        });

        return  new FactureDetailNormalize(res);
    }

    factureNormalizer(data: {}): FactureNormalize {
        const res = {};

        Object.keys(data).forEach(elem => {
            const tmp = elem.replace('fcl_', '');
            if (elem === 'details')
                res[tmp] = data[elem].map(item => this.factureDetailNormalizer(item));
            else
                res[tmp] = data[elem];
        });

        const fac = new FactureNormalize(res);
        fac.factureType = 'client';
        return fac;
    }

    createFactureClient(data): Observable<ApiResponse> {
        return this.apiService.post('facture-client', JSON.stringify(data));
    }

    updateFactureClient(fclId, data): Observable<ApiResponse> {
        return this.apiService.put(`facture/client/${fclId}`, JSON.stringify(data));
    }

    updateFactureClientDetail(fcdId, data): Observable<ApiResponse> {
        return this.apiService.put(`facture/client/detail/${fcdId}`, data);
    }

    copyFacture(fclId): Observable<ApiResponse> {
        return this.apiService.post(`facture/client/copy/${fclId}`, []);
    }

    updateFcdClasseByArticleList(data): Observable<ApiResponse> {
        return this.apiService.put(`facture/client/detail-article-list/`, JSON.stringify(data));
    }

    updateFcdClasseByArticle(data): Observable<ApiResponse> {
        return this.apiService.put(`facture/client/detail-article/`, data.toJSON());
    }

    contrePassation(id): Observable<ApiResponse> {
        return this.apiService.delete(`facture/client/${id}`);
    }

    saveFusionFactureMontants(data): Observable<ApiResponse> {   
        return this.apiService.post('facture/client/fusion/montants/', JSON.stringify(data));
    }

    updateIdFusionFacture(data): Observable<ApiResponse> {
        return this.apiService.post('facture/client/fusion/update/', JSON.stringify(data));
    }

    getFusionFacture(id): Observable<ApiResponse> {
        return this.apiService.get(`facture/client/fusion/${id}`);
    }

    getAllFusionFactures(): Observable<ApiResponse> {
        return this.apiService.get(`facture/client/fusion/`);
    }

    getAllFusionFacturesDetail(): Observable<ApiResponse> {
        return this.apiService.get(`facture/client/fusion/detail`);
    }

    generatePdf(id): Observable<ApiResponse> {
        return  this.apiService.get(`facture/client/pdf/${id}`);
    }

    generateFusionPdf(id): Observable<ApiResponse> {
        const params = {};
        params['fusion'] = true;

        return  this.apiService.get(`facture/client/fusion/pdf/${id}`, null, params);
    }

    generateNoteCredit(data): Observable<ApiResponse> {
        return  this.apiService.post(`facture/client/note-credit/`, JSON.stringify(data));
    }

    hasNoteCredit(id): Observable<ApiResponse> {
        const params = {};

        if (id)
            params['id'] = id;

        return this.apiService.get('facture/client/note', null, params);
    }
    envoiEmail( data: any): Observable<ApiResponse> {
        return this.apiService.post(`envoi-facture/`, JSON.stringify(data));
    }
    exportCsv(bureauId, data: any): Observable<ApiResponse> {
        return  this.apiService.post(`facture/client/exportIbs/${bureauId}`, JSON.stringify(data));
    }

    exportReleve(bureauId, params: FactureSearchTerms): Observable<ApiResponse> {
        return  this.apiService.get(`facture/client/exportReleve/${bureauId}`, null, params);
    }

    exportBob(bureauId, data: any): Observable<ApiResponse> {
        return  this.apiService.post(`facture/client/exportBob/${bureauId}`, JSON.stringify(data));
    }

    exportBobDetail(bureauId, data: any): Observable<Blob> {
        return  this.apiService.post(`facture/client/exportBobDetail/${bureauId}`, JSON.stringify(data), {responseType: 'blob'});
    }

    exportExact(bureauId, data: any): Observable<Blob> {
        return  this.apiService.post(`facture/client/exportExact/${bureauId}`, JSON.stringify(data), {responseType: 'blob'});
    }

    exportIntegrix(bureauId, data: any): Observable<ApiResponse> {
        return this.apiService.post(`facture/client/exportIntegrix/${bureauId}`, JSON.stringify(data));
    }

    exportOptimis(bureauId, data: any): Observable<ApiResponse> {
        return this.apiService.post(`facture/client/exportOptimis/${bureauId}`, JSON.stringify(data));
    }

    exportFacture(bureauId, data: any): Observable<ApiResponse> {
        return this.apiService.post(`facture/client/export/${bureauId}`, JSON.stringify(data));
    }

    exportWinbooksBe(bureauId, data: any): Observable<Blob> {
        return this.apiService.post(`facture/client/exportWinbooksBe/${bureauId}`, JSON.stringify(data), {responseType: 'blob'});
    }

    exportSage(bureauId, data: any): Observable<ApiResponse> {
        return this.apiService.post(`facture/client/exportSage/${bureauId}`, JSON.stringify(data));
    }

    getExportMethodes(): Observable<ApiResponse> {
        return this.apiService.get(`methode-export`, null, {actif: 1});
    }

    generateGlobalPdf(data: {arrayId: string|number[], type: string, choice: string, filters: FactureSearchTerms}): Observable<Blob> {
        return  this.apiService.post(`facture/client/pdf-multiple`, JSON.stringify(data), {responseType: 'blob'});
    }

    renumerotationFacture(bureauxId, data: {year: number, start_increment_at: number, start_from_ref: number}): Observable<ApiResponse> {
        return  this.apiService.post(`facture/client/renumerotation/${bureauxId}`, JSON.stringify(data));
    }

    reinitialisationExportFactures(data): Observable<ApiResponse> {
        return  this.apiService.post(`facture/client/export/reinitialisation/`, JSON.stringify(data));
    }
    
    regeneratePdf(data): Observable<ApiResponse> {
        return  this.apiService.post(`facture/client/pdf/regenerate/`, JSON.stringify(data));
    }
    
    getTranslations(): Observable<ApiResponse> {
        return this.apiService.get(`facture/client/translations/display`);
    }

    updateTranslations(data): Observable<ApiResponse> {
        return this.apiService.post(`facture/client/translations`, JSON.stringify(data));
    }

    generateNewRef(bureauId, emetteur?) {
        const base = `facture/client/reference/${bureauId}`;
        const suffix = (emetteur !== undefined && emetteur !== null && `${emetteur}` !== '') ? `/emetteur/${emetteur}` : '';
        return this.apiService.get(`${base}${suffix}`);
    }
    
    getFacturesByIds(data): Observable<ApiResponse> {
        let params = new HttpParams().set('ids', data.join(','));

        return this.apiService.get(`facture/client/coda/liste`, { params });
    }
}
